import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import type { TeamId } from '../utils/rbac';
import { FULFILLMENT_ESCALATIONS } from '../data/fulfillmentEscalations';
import { PROXY_SEED_TODAY } from '../data/mockProxyJobs';

// In-app hand-offs between Fulfillment (wayne) and Proxy (cary). Both teams
// work in Ace, so they don't email each other: an escalation or an update is
// a message that shows in the other team's bell, and Ace turns the ones that
// need work into Intake Queue tasks. Email is only for outside parties.
export type TeamMessageKind =
  | 'received-short'     // Fulfillment → Proxy: a delivery came in short
  | 'no-job-found'       // Fulfillment → Proxy: material with no matching job
  | 'escalation'         // either way: a person escalated a receipt
  | 'shortage-update'    // Proxy → Fulfillment: where the shortage chase is
  | 'material-on-the-way' // Proxy → Fulfillment: merge it when it arrives
  | 'material-merged';   // Fulfillment → Proxy: merged, close the ticket

export interface TeamMessage {
  id: string;
  from: TeamId;
  to: TeamId;
  kind: TeamMessageKind;
  title: string;
  body: string;
  at: string;          // "YYYY-MM-DD HH:MM"
  jobId?: string;
  scan?: string;
  client?: string;
}

interface TeamMessagesValue {
  messages: TeamMessage[];
  send: (message: Omit<TeamMessage, 'id' | 'at'>) => void;
  isRead: (id: string) => boolean;
  markRead: (ids: string[]) => void;
}

const LS_KEY = 'opsai.teammessages.v1';
const READ_KEY = 'opsai.teammessages.read.v1';

// "Today · 8:50 AM" (the escalation fixtures' display date) → "2026-06-01 08:50".
function toStamp(display: string): string {
  const m = display.match(/^(Today|Yesterday|(\d+) days? ago)\s*·\s*(\d+):(\d+)\s*(AM|PM)/);
  if (!m) return `${PROXY_SEED_TODAY} 09:00`;
  const daysAgo = m[1] === 'Today' ? 0 : m[1] === 'Yesterday' ? 1 : Number(m[2]);
  const d = new Date(`${PROXY_SEED_TODAY}T00:00:00`);
  d.setDate(d.getDate() - daysAgo);
  let hour = Number(m[3]) % 12;
  if (m[5] === 'PM') hour += 12;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(hour)}:${m[4]}`;
}

// The hand-offs the backend seed already treats as done (receipts marked
// Received Short or No Job Found and escalated to Proxy).
const SEED_MESSAGES: TeamMessage[] = FULFILLMENT_ESCALATIONS.map(esc => ({
  id: `seed-${esc.id}`,
  from: 'wayne',
  to: 'cary',
  kind: esc.jobNumber ? 'received-short' : 'no-job-found',
  title: esc.jobNumber ? `Received short: ${esc.client}` : `No job found: ${esc.client}`,
  body: esc.body,
  at: toStamp(esc.date),
  jobId: esc.jobNumber,
  scan: esc.scan,
  client: esc.client,
}));

function load<T>(key: string, fallback: T): T {
  try { const v = JSON.parse(window.localStorage.getItem(key) || 'null'); return v ?? fallback; } catch { return fallback; }
}

function nowStamp(): string {
  const t = new Date();
  return `${PROXY_SEED_TODAY} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
}

const TeamMessagesContext = createContext<TeamMessagesValue | null>(null);

export function TeamMessagesProvider({ children }: { children: ReactNode }) {
  const [sent, setSent] = useState<TeamMessage[]>(() => load(LS_KEY, []));
  const [read, setRead] = useState<string[]>(() => load(READ_KEY, []));

  const messages = useMemo(() => [...SEED_MESSAGES, ...sent].sort((a, b) => b.at.localeCompare(a.at)), [sent]);

  const send = useCallback((message: Omit<TeamMessage, 'id' | 'at'>) => {
    setSent(prev => {
      const next = [...prev, { ...message, id: `tm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`, at: nowStamp() }];
      try { window.localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, []);

  const isRead = useCallback((id: string) => read.includes(id), [read]);
  const markRead = useCallback((ids: string[]) => {
    setRead(prev => {
      const next = Array.from(new Set([...prev, ...ids]));
      try { window.localStorage.setItem(READ_KEY, JSON.stringify(next)); } catch { /* still works for this session */ }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ messages, send, isRead, markRead }), [messages, send, isRead, markRead]);
  return <TeamMessagesContext.Provider value={value}>{children}</TeamMessagesContext.Provider>;
}

export function useTeamMessages(): TeamMessagesValue {
  const ctx = useContext(TeamMessagesContext);
  if (!ctx) throw new Error('useTeamMessages must be used inside TeamMessagesProvider');
  return ctx;
}
